import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import AccountDeleteInstructions from '../types/instructions/AccountDeleteInstructions'
import TransactionType from '../types/TransactionType'

/**
 * An annotation for AccountDelete transactions.
 *
 * @property type - The transaction type.
 * @property destination - An explanation of the destination.
 * @property destinationTag - An explanation of the destinationTag.
 */
export interface AccountDeleteAnnotation {
  readonly type: TransactionType.AccountDelete
  readonly destination: Explanation<string>
  readonly destinationTag?: Explanation<number>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: AccountDeleteInstructions,
): AccountDeleteAnnotation {
  const destination = explain(
    transactionInstructions.Destination,
    `After deleting the account that sent this transaction, any leftover XRP will be sent to ${cffd(
      transactionInstructions.Destination,
      'destination',
    )}`,
  )

  const destinationTag = explainOptional(
    transactionInstructions.DestinationTag,
    (tag) =>
      `This tag identifies a hosted recipient or other information for the recipient of the deleted account's leftover XRP: ${cffd(
        tag.toString(),
        'destinationTag',
      )}`,
  )

  return {
    destination,
    ...(destinationTag && { destinationTag }),

    type: TransactionType.AccountDelete,
  }
}
